import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  Stack,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Drawer,
  Button,
  Tooltip,
  Divider,
  Avatar,
} from '@mui/material';
import {
  ShieldAlert,
  Search,
  Eye,
  X,
  Filter,
  UserCheck,
  CreditCard,
  RefreshCw,
  ArrowLeft,
  Clock,
  Globe,
  Terminal,
} from 'lucide-react';
import { toast } from 'react-toastify';
import Page from '../components/Page';
import { getAuditLogs } from '../api/audit.api';

const getActionColor = (action) => {
  if (!action) return 'default';
  const act = action.toUpperCase();
  if (act.includes('FUND') || act.includes('CREDIT')) return 'success';
  if (act.includes('DEDUCT') || act.includes('DEBIT')) return 'warning';
  if (act.includes('REFUND')) return 'info';
  if (act.includes('BLOCK') || act.includes('DELETE') || act.includes('REMOVE')) return 'error';
  return 'primary';
};

export default function AuditLogsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const actorIdParam = searchParams.get('actorId') || '';
  const searchParam = searchParams.get('search') || '';

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [searchQuery, setSearchQuery] = useState(searchParam);
  const [selectedAction, setSelectedAction] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 15,
        search: searchQuery || undefined,
        action: selectedAction || undefined,
        actorId: actorIdParam || undefined,
      };

      const res = await getAuditLogs(params);
      if (res.ok && res.data?.success) {
        const data = res.data.data;
        setLogs(data.logs || []);
        setTotalPages(data.pagination?.pages || 1);
        setTotalCount(data.pagination?.total || 0);
      } else {
        toast.error(res.data?.message || 'Failed to fetch audit logs');
      }
    } catch (err) {
      toast.error('Error connecting to audit logs service');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSearchQuery(searchParam || '');
  }, [searchParam]);

  useEffect(() => {
    fetchLogs();
  }, [page, selectedAction, actorIdParam, searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchLogs();
  };

  const handleOpenDetails = (log) => {
    setSelectedLog(log);
    setDrawerOpen(true);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedAction('');
    setSearchParams({});
    setPage(1);
  };

  return (
    <Page title="Action Audit Trail">
      <Box sx={{ minHeight: '100vh', bgcolor: '#fbfbfd', pb: 10 }}>
        <Container maxWidth="xl" sx={{ pt: { xs: 3, md: 4 } }}>
          {/* Header */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 4 }}
          >
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center">
                {actorIdParam && (
                  <IconButton onClick={() => navigate('/dashboard/staff')} sx={{ bgcolor: '#ffffff', border: '1px solid #e0e0e0' }}>
                    <ArrowLeft size={18} />
                  </IconButton>
                )}
                <Typography variant="h4" fontWeight={800} color="#1d1d1f" sx={{ letterSpacing: '-0.02em' }}>
                  {actorIdParam ? 'Staff Member Activity Log' : 'Action Audit Trail'}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Comprehensive tracking of all staff and admin actions, manual deposits, refunds, and user modifications.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<RefreshCw size={16} />}
              onClick={fetchLogs}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Refresh Logs
            </Button>
          </Stack>

          {/* Stats Bar */}
          <Grid container spacing={2.5} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, bgcolor: '#ffffff', border: '1px solid #e0e0e0' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                  Total Actions Tracked
                </Typography>
                <Typography variant="h4" fontWeight={800} color="#1d1d1f" sx={{ mt: 0.5 }}>
                  {totalCount.toLocaleString()}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, bgcolor: '#ffffff', border: '1px solid #e0e0e0' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                  Manual Funding Actions
                </Typography>
                <Typography variant="h4" fontWeight={800} color="success.main" sx={{ mt: 0.5 }}>
                  {logs.filter((l) => l.action && l.action.includes('FUND')).length}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, bgcolor: '#ffffff', border: '1px solid #e0e0e0' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                  Transaction Refunds
                </Typography>
                <Typography variant="h4" fontWeight={800} color="info.main" sx={{ mt: 0.5 }}>
                  {logs.filter((l) => l.action && l.action.includes('REFUND')).length}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, bgcolor: '#ffffff', border: '1px solid #e0e0e0' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                  Deductions & Overrides
                </Typography>
                <Typography variant="h4" fontWeight={800} color="warning.main" sx={{ mt: 0.5 }}>
                  {logs.filter((l) => l.action && (l.action.includes('DEDUCT') || l.action.includes('BLOCK'))).length}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Search & Filter Toolbar */}
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, mb: 4, bgcolor: '#ffffff', border: '1px solid #e0e0e0' }}>
            <form onSubmit={handleSearch}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                <TextField
                  placeholder="Search staff actor, target user, action..."
                  size="small"
                  fullWidth
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: <Search size={18} color="#999" style={{ marginRight: 8 }} />,
                  }}
                />

                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Filter by Action</InputLabel>
                  <Select
                    value={selectedAction}
                    label="Filter by Action"
                    onChange={(e) => {
                      setSelectedAction(e.target.value);
                      setPage(1);
                    }}
                  >
                    <MenuItem value="">All Action Types</MenuItem>
                    <MenuItem value="MANUAL_WALLET_FUND">Manual Wallet Fund</MenuItem>
                    <MenuItem value="MANUAL_WALLET_DEDUCT">Manual Wallet Deduct</MenuItem>
                    <MenuItem value="TRANSACTION_REFUND">Transaction Refund</MenuItem>
                    <MenuItem value="USER_BLOCK">User Block</MenuItem>
                    <MenuItem value="USER_UNBLOCK">User Unblock</MenuItem>
                  </Select>
                </FormControl>

                <Stack direction="row" spacing={1}>
                  <Button variant="contained" type="submit" sx={{ bgcolor: '#1d1d1f', color: 'white', textTransform: 'none', px: 3 }}>
                    Search
                  </Button>
                  {(searchQuery || selectedAction || actorIdParam) && (
                    <Button variant="text" onClick={handleClearFilters} sx={{ textTransform: 'none', color: 'text.secondary' }}>
                      Clear Filters
                    </Button>
                  )}
                </Stack>
              </Stack>
            </form>
          </Paper>

          {/* Audit Logs Table */}
          <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #e0e0e0', bgcolor: '#ffffff' }}>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: '#fafafa' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: '#666' }}>Timestamp</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#666' }}>Staff / Admin Actor</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#666' }}>Action Type</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#666' }}>Target User / Resource</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#666' }}>Summary & Reason</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#666' }}>
                      Inspect
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {(() => {
                    if (loading) {
                      return (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                            <Typography variant="body2" color="text.secondary">
                              Loading action audit trail...
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    }
                    if (logs.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                            <Typography variant="body2" color="text.secondary">
                              No audit action logs recorded.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    }
                    return logs.map((log) => (
                      <TableRow key={log.id} hover>
                        {/* Timestamp */}
                        <TableCell sx={{ fontSize: '0.85rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Clock size={14} color="#888" />
                            <Box>
                              <Typography variant="body2" fontWeight={600} color="#1d1d1f">
                                {new Date(log.createdAt).toLocaleDateString()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(log.createdAt).toLocaleTimeString()}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        {/* Staff / Admin Actor */}
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar sx={{ width: 32, height: 32, bgcolor: '#eef2ff', color: '#4f46e5', fontSize: '0.8rem', fontWeight: 700 }}>
                              {(log.actorName || log.actorEmail || 'A').substring(0, 2).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" fontWeight={700} color="#1d1d1f">
                                {log.actorName || 'Admin'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {log.actorEmail}
                              </Typography>
                            </Box>
                            <Chip size="small" label={log.actorRole || 'staff'} variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                          </Stack>
                        </TableCell>

                        {/* Action Type */}
                        <TableCell>
                          <Chip
                            size="small"
                            label={(log.action || '').replace(/_/g, ' ')}
                            color={getActionColor(log.action)}
                            sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                          />
                        </TableCell>

                        {/* Target */}
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} color="#1d1d1f">
                            {log.targetName || `Target #${log.targetId}`}
                          </Typography>
                          {log.targetType && (
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                              Type: {log.targetType}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Summary & Reason */}
                        <TableCell sx={{ maxWidth: 280 }}>
                          <Typography variant="body2" noWrap color="text.secondary">
                            {log.details?.reason || log.details?.reference || (log.details?.amount ? `Amount: ₦${log.details.amount.toLocaleString()}` : 'No details')}
                          </Typography>
                        </TableCell>

                        {/* Inspect Details */}
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleOpenDetails(log)}>
                            <Eye size={18} color="#666" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ));
                  })()}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #f0f0f0' }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, p) => setPage(p)}
                  shape="rounded"
                />
              </Box>
            )}
          </Paper>
        </Container>

        {/* ─── Inspection Drawer ─── */}
        <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <Box sx={{ width: { xs: 320, sm: 460 }, p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight={800}>
                Action Audit Record
              </Typography>
              <IconButton size="small" onClick={() => setDrawerOpen(false)}>
                <X size={20} />
              </IconButton>
            </Stack>

            {selectedLog && (
              <Stack spacing={3}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, bgcolor: '#fbfbff' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                    Action Performed
                  </Typography>
                  <Typography variant="h6" fontWeight={800} color="#1d1d1f" sx={{ mt: 0.5, mb: 1 }}>
                    {selectedLog.action}
                  </Typography>
                  <Chip size="small" label={getActionColor(selectedLog.action).toUpperCase()} color={getActionColor(selectedLog.action)} sx={{ fontWeight: 700 }} />
                </Paper>

                <Stack spacing={2} sx={{ p: 2.5, bgcolor: '#fafafa', borderRadius: 3 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Staff Member Identity
                  </Typography>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Full Name
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedLog.actorName || 'N/A'}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Email Address
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedLog.actorEmail || 'N/A'}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Staff Role
                    </Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ textTransform: 'capitalize' }}>
                      {selectedLog.actorRole || 'Staff'}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Actor ID
                    </Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                      {selectedLog.actorId}
                    </Typography>
                  </Stack>
                </Stack>

                <Stack spacing={2} sx={{ p: 2.5, bgcolor: '#fafafa', borderRadius: 3 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Target Resource
                  </Typography>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Target Name
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedLog.targetName || 'N/A'}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Target ID
                    </Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                      {selectedLog.targetId || 'N/A'}
                    </Typography>
                  </Stack>
                </Stack>

                {selectedLog.details && (
                  <Box sx={{ p: 2.5, border: '1px solid #e0e0e0', borderRadius: 3 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                      Action Payload Details
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: '#1e1e1e',
                        color: '#4ec9b0',
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        overflowX: 'auto',
                      }}
                    >
                      <pre style={{ margin: 0 }}>{JSON.stringify(selectedLog.details, null, 2)}</pre>
                    </Paper>
                  </Box>
                )}
              </Stack>
            )}
          </Box>
        </Drawer>
      </Box>
    </Page>
  );
}
